import { useSyncExternalStore } from 'react';
import { ldLog } from '../../utils/ui/loginDebug';

/**
 * Sign-in handoff hold (2026-09-25). Login raises it from the moment Sign in is
 * pressed until it navigates away, so its success beat and the wordmark →
 * splash morph can actually play.
 *
 * Why it exists: the instant SIGNED_IN lands, LocaleContext starts fetching
 * the user's saved language and reports `localeReady = false`. BootGate used
 * to treat that like a cold boot: it rendered null — tearing Login down
 * mid-choreography — and threw the splash up immediately, so the founder
 * saw "the splash right away, no morph anywhere". While this hold is up, the
 * language wait alone no longer blanks the Login route; while the language
 * lock is up, LocaleContext keeps the language already on screen.
 *
 * UX only — BootGate is a readiness gate, not an authorization boundary
 * (ProtectedRoute / PlatformRoute / RLS are). Module-level so both consumers
 * (BootGate, LocaleProvider) read it without a new provider.
 */
let held = false;
// Language lock: while the card's text is VISIBLE (submit, beat, 2FA step),
// the signed-in user's saved language must not flip it mid-read. Login drops
// the lock at 'leaving', when the splash already covers the card.
let localeLocked = false;
const listeners = new Set();

export function setAuthHandoffHold(next, { lockLocale = false } = {}) {
  const value = Boolean(next);
  const lock = value && Boolean(lockLocale);
  if (value === held && lock === localeLocked) return;
  held = value;
  localeLocked = lock;
  ldLog('handoff', { held, lock });
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useAuthHandoffHold() {
  return useSyncExternalStore(subscribe, () => held, () => false);
}

export function useAuthHandoffLocaleLock() {
  return useSyncExternalStore(subscribe, () => localeLocked, () => false);
}

/**
 * Handoff BUSY signal (2026-10-21, desktop-Safari blank). Narrower than the
 * hold above: up from Sign in until the 2FA code is VERIFIED (the whole 2FA
 * step stays busy, nothing but the 2FA decision runs before the code) or, on
 * a password-only sign-in, until the success redirect starts ('leaving').
 * While it is up, non-essential work that SIGNED_IN would otherwise kick off
 * (role-home chunk prefetch, company-settings / catalog / receipts reads,
 * realtime channels, dock chunks, the last-login stamp) WAITS via
 * `runAfterAuthHandoff` -- a long task there blanked the whole page in real
 * desktop Safari. Decisions (profile role, factors, AAL) never wait.
 * UX/perf only; nothing here is an authorization boundary.
 */
let busy = false;
let deferred = [];
let draining = false;
const busyListeners = new Set();
const BUSY_FAILSAFE_MS = 5000;
let failsafe = null;
// A hidden tab pauses rAF: release on a 0ms timer instead so nothing stays queued.
const nextFrame = (fn) => (typeof requestAnimationFrame === 'function' && !(typeof document !== 'undefined' && document.hidden)
  ? requestAnimationFrame(fn) : setTimeout(fn, 0));

function drainDeferred() {
  if (draining) return;
  draining = true;
  // One item per frame: the released work must not itself become the next long task.
  const step = () => {
    if (busy || deferred.length === 0) { draining = false; return; }
    const entry = deferred.shift();
    ldLog('handoff.drain', { left: deferred.length });
    try { entry.fn(); } catch (err) { console.error('Deferred handoff work failed:', err); }
    nextFrame(step);
  };
  nextFrame(step);
}

/**
 * `failsafeMs` (default 5s): the safety release if nothing ever clears BUSY.
 * The 2FA step passes a long one -- a person typing a code must not have the
 * queue drain under them. Passing it while already busy re-arms the timer;
 * a bare call while already busy changes nothing.
 */
export function setAuthHandoffBusy(next, { failsafeMs } = {}) {
  const value = Boolean(next);
  if (value === busy) {
    if (busy && failsafeMs !== undefined) {
      if (failsafe) clearTimeout(failsafe);
      failsafe = setTimeout(() => { failsafe = null; setAuthHandoffBusy(false); }, failsafeMs);
    }
    return;
  }
  busy = value;
  ldLog('handoff.busy', { busy, queued: deferred.length });
  if (failsafe) { clearTimeout(failsafe); failsafe = null; }
  if (busy) failsafe = setTimeout(() => { failsafe = null; setAuthHandoffBusy(false); }, failsafeMs ?? BUSY_FAILSAFE_MS);
  busyListeners.forEach((l) => l());
  if (!busy && deferred.length) drainDeferred();
}

export function isAuthHandoffBusy() { return busy; }

/**
 * Runs `fn` now when no sign-in handoff is in progress (the normal case:
 * synchronous, identical to calling it), otherwise once the handoff settles.
 * `first` queues ahead of everything else (security watchers).
 * Returns a cancel function -- call it from the effect cleanup.
 */
export function runAfterAuthHandoff(fn, { first = false } = {}) {
  if (!busy) { fn(); return () => {}; }
  const entry = { fn };
  if (first) deferred.unshift(entry); else deferred.push(entry);
  return () => { deferred = deferred.filter((e) => e !== entry); };
}

export function useAuthHandoffBusy() {
  return useSyncExternalStore((l) => { busyListeners.add(l); return () => busyListeners.delete(l); }, () => busy, () => false);
}

/** @internal test helper */
export function __resetAuthHandoffHoldForTests() {
  held = false; localeLocked = false; listeners.clear();
  busy = false; deferred = []; draining = false; busyListeners.clear();
  if (failsafe) { clearTimeout(failsafe); failsafe = null; }
}
